<template>
  <ProductCard
    :id="product.id"
    class="col-span-12 md:col-span-4 2xl:col-span-3"
    :product="product"
    :variant-price="selectedVariant?.price"
    :image="image"
    :disable-hover-image="true"
    wishlist-remove-cta
  >
    <template #description="{ brand, name, price, isAvailable }">
      <div>
        <div class="absolute right-4 top-2">
          <p v-if="!isAvailable" class="font-default text-md pr-3">
            {{ $t('global.sold_out') }}
          </p>
        </div>
        <div :class="{ 'opacity-50': !isAvailable }">
          <div class="mt-2 w-full justify-between py-2 text-sm">
            <p class="font-default text-md">{{ brand }}</p>
            <p class="font-default text-md">{{ name }}</p>
          </div>
          <div>
            <ProductPrice
              v-if="price"
              :price="price"
              :applied-reductions="price ? price.appliedReductions : []"
              :show-price-reduction="showPriceReduction"
              type="whisper"
              size="md"
            />
          </div>
          <div v-if="!isInBasket && isAvailable">
            <div
              v-if="isChangingSize"
              :key="product.id"
              class="flex items-center space-x-2 py-4"
            >
              <ProductSizePicker
                :product="product"
                class="justify-center"
                :variants="product.variants ?? []"
                :selected-variant="selectedVariant"
                @select-size="selectSize"
              />
            </div>
          </div>
          <div class="space-x-1">
            <span v-if="sizeFromVariant" class="text-base font-bold">
              {{ sizeCategoryLabel }}

              {{ sizeFromVariant }}
            </span>
            <button
              v-if="!isInBasket && isAvailable && !isChangingSize"
              class="mt-4 inline-flex items-center gap-2 whitespace-nowrap text-base leading-5 tracking-wide underline transition duration-200 ease-linear"
              data-test-id="add-item-to-wishlist-button"
              @click="isChangingSize = !isChangingSize"
              v-text="
                selectedVariant
                  ? $t('wishlist_card.change_size_label')
                  : $t('wishlist.change_size_label')
              "
            />
          </div>
          <Button
            class="mt-2"
            :disabled="isInBasket || !isAvailable"
            data-test-id="add-item-to-basket-button"
            @click="$emit('click:add-to-cart')"
          >
            {{ $t('pdp.add_label') }}
          </Button>
        </div>
        <LazyProductSizeGuideModal
          v-if="isProductSizeGuideModalOpen"
          :product="product"
        />
      </div>
    </template>
  </ProductCard>
</template>

<script setup lang="ts">
import {
  type WishlistItem,
  getFirstAttributeValue,
  getVariant,
  type ProductImage as ProductImageType,
  type Variant,
} from '@scayle/storefront-nuxt'
import { getSizeLabel } from '#layers/base/types/variant'

const props = withDefaults(
  defineProps<{
    item: WishlistItem
    isInBasket?: boolean
    showPriceReduction?: boolean
    image?: ProductImageType
  }>(),
  { isInBasket: true, showPriceReduction: true, image: undefined },
)

const product = computed(() => props.item.product)

const selectedVariant = computed(() => {
  return getVariant(product.value.variants ?? [], props.item.variant?.id ?? 0)
})
const emit = defineEmits(['click:add-to-cart', 'click:change-size'])

const isChangingSize = ref(false)
const { isProductSizeGuideModalOpen } = useUiState()

const sizeCategoryLabel = computed(
  () =>
    getFirstAttributeValue(selectedVariant.value?.attributes, 'sizeCategory')
      ?.label || '',
)
const selectSize = (variant: Variant) => {
  emit('click:change-size', variant)
  isChangingSize.value = false
}

const sizeFromVariant = computed(() => {
  if (!selectedVariant.value) {
    return
  }
  return getSizeLabel(selectedVariant.value) || ''
})
</script>
